import { Card, Form } from "react-bootstrap";
import TrafficSourcesAutocomplete from "../components/autocompletes/trafficSources/TrafficSourcesAutocomplete";
import { FiltersInitialStateHelper } from "./FiltersInitialState";
import { FiltersEventsHelper, isDateGroupBy } from "./FiltersEventsHelper";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Alert from 'react-bootstrap/Alert';
import DateRangeSelector from "../components/DateRangeSelector";
import CountriesAutocomplete from "../components/autocompletes/countries/CountriesAutocomplete";
import TrafficTypeAutocomplete from "../components/autocompletes/trafficTypes/TrafficTypeAutocomplete";
import PlacementsAutocomplete from "../components/autocompletes/placements/PlacementsAutocomplete";
import GroupByAutocomplete from "../components/autocompletes/groupBys/GroupByAutocomplete";
import ProductsAutocomplete from "../components/autocompletes/products/ProductsAutocomplete";
import CampaignsAutocomplete from "../components/autocompletes/campaigns/CampaignsAutocomplete";
import DemandSourcesAutocomplete from "../components/autocompletes/demandSources/DemandSourcesAutocomplete";
import CampaignStatsAutocomplete from "../components/autocompletes/campaignStats/CampaignStatsAutocomplete";
import CampaignHealthAutocomplete from "../components/autocompletes/campaignHealth/CampaignHealthAutocomplete";
import ActivityStatusAutocomplete from "../components/autocompletes/activityStatus/ActivityStatusAutocomplete";
import ClinksStatusAutocomplete from "../components/autocompletes/clinksStatus/ClinksStatusAutocomplete";
import { TrafficSourceResponseDto, ResponseStatus,ProductsCampaignsDto,UserPermissions, QueryFlags, SortState, DateModes, IdName, ColumnIds, IncludeExclude, DemandSourcesResponseDto, TrafficTypeMap } from "../utils/types";
import { PageState, PublisherOrSiteOption,ProductRecord,PlacementResponseDto,CLinkOwnerResponseDto,CLinkOwnerOption } from "../utils/types";
import { PlacementRecord, DemandSourceRecord, CountryOption, CampaignStatsOption, CampaignHealthOption, ActivityStatusOption, ClinksStatusOption } from "../utils/types";
import { getGroupByArray} from "../utils/misc";
import { CommonRequests } from "../common/CommonRequests";
import StateHelper from "../utils/StateHelper";
import CLinkOwnersAutocomplete from "../components/autocompletes/clinkOwners/CLinkOwnersAutocomplete";
import QueryFlagsAutocomplete from "../components/autocompletes/queryFlags/QueryFlagsAutocomplete";
import dayjs from "../utils/dayjs";
import { PagesHelper } from "./PagesHelper";
import LoadingMessage from "../components/notifications/LoadingMessage";
import { isAllowedFilterField } from "../utils/UserHelper";
import InfoIcon from "../components/clinks/info/InfoIcon";

function UnableToLoadMessage (props : any) {
  const alertStyle = {marginBottom : '1px', height: '30px', lineHeight:'30px' ,padding: '0px 15px'};
  const fontStyle = { fontSize: '90%', color : 'red'};
  return <Alert variant="dark" style={alertStyle} className="border border-danger">
        <span style={fontStyle}>{props.message}</span> 
      </Alert>;
}

function placementsOnChange(selectedValues: PlacementRecord[], page : string) {
  const pageState : PageState = StateHelper.getPageState();

  if (selectedValues.length > 0) {
    pageState.placementIds = selectedValues.map(p => p.publisherPlacementId);
  } else {
    pageState.placementIds = [];
  }

  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function trafficTypeOnChange(selectedValues: any, page : string) {
  const pageState : PageState = StateHelper.getPageState();

  if (selectedValues.length > 0) {
    // Expand all-mobile and all-desktop, then get unique values
    const expandedTypes = selectedValues
      .map((val: any) => TrafficTypeMap[val.id])
      .join(",")
      .split(",");

    // Remove duplicates
    const uniqueTypes = Array.from(new Set(expandedTypes));

    pageState.trafficTypes = uniqueTypes.join(",");
  } else {
    pageState.trafficTypes = null;
  }

  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function countryOnChange(selectedValues: any, page : string) {
  const pageState : PageState = StateHelper.getPageState();
  const selectedIds = selectedValues.map((val: any) => val.id);
  pageState.countries = selectedIds.length > 0 ? selectedIds.join(",") : null;
  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function dateRangeOnChange(start: dayjs.Dayjs, end: dayjs.Dayjs, dateMode: DateModes) {
  const pageState : PageState = StateHelper.getPageState();
  const sortState:SortState = StateHelper.getSortState();
  FiltersEventsHelper.dateRangeOnChange(pageState.page, pageState, sortState, start, end, dateMode);
}

function trafficSourceOnChange(selectedValues: PublisherOrSiteOption[], page : string, isSelectedFromCheckbox?: boolean, allOptions?: PublisherOrSiteOption[]) {
  const pageState : PageState = StateHelper.getPageState();

  // Group selected items by publisher to check if all sites are selected
  const selectedByPublisher = new Map<string, Set<string>>();

  selectedValues.forEach(v => {
    const parts = v.key.split(":");
    const publisherId = parts[0] || "";
    const siteId = parts.length > 1 ? parts[1] : "";

    if (!publisherId) return;

    if (!selectedByPublisher.has(publisherId)) {
      selectedByPublisher.set(publisherId, new Set());
    }

    if (siteId && siteId !== "<all sites>") {
      selectedByPublisher.get(publisherId)!.add(siteId);
    } else if (siteId === "<all sites>") {
      // Mark this publisher as having all sites selected explicitly
      selectedByPublisher.get(publisherId)!.add("<all sites>");
    }
  });

  // Get all available sites per publisher from props.options
  const allSitesByPublisher = new Map<string, Set<string>>();
  if (allOptions) {
    allOptions.forEach(opt => {
      const parts = opt.key.split(":");
      const publisherId = parts[0] || "";
      const siteId = parts.length > 1 ? parts[1] : "";

      if (!publisherId || !siteId || siteId === "<all sites>") return;

      if (!allSitesByPublisher.has(publisherId)) {
        allSitesByPublisher.set(publisherId, new Set());
      }
      allSitesByPublisher.get(publisherId)!.add(siteId);
    });
  }

  // Build publisherIds and siteIds based on whether all sites are selected
  const publisherIds: string[] = [];
  const siteIds: string[] = [];

  selectedByPublisher.forEach((selectedSites, publisherId) => {
    // Check if "<all sites>" marker is present or if all available sites are selected
    const hasAllSitesMarker = selectedSites.has("<all sites>");
    const allAvailableSites = allSitesByPublisher.get(publisherId);
    const allSitesSelected = allAvailableSites &&
                              selectedSites.size > 0 &&
                              !hasAllSitesMarker &&
                              Array.from(selectedSites).every(siteId => allAvailableSites.has(siteId)) &&
                              selectedSites.size === allAvailableSites.size;

    if (hasAllSitesMarker || allSitesSelected) {
      // All sites for this publisher are selected → send only publisherId
      publisherIds.push(publisherId);
    } else {
      // Only some sites selected → send individual siteIds
      selectedSites.forEach(siteId => {
        if (siteId !== "<all sites>") {
          siteIds.push(siteId);
        }
      });
    }
  });

  pageState.publisherIds = publisherIds;
  pageState.siteIds = siteIds;

  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function groupByOnChange(selectedValues: IdName[], page : string) {
  const pageState : PageState = StateHelper.getPageState();
  const sortState:SortState = StateHelper.getSortState();
  FiltersEventsHelper.groupByOnChange(page, pageState, sortState, selectedValues);
}

function productsOnChange(selectedValues: ProductRecord[], page : string) {
  const pageState : PageState = StateHelper.getPageState();
  pageState.productIds = selectedValues.length > 0 ? selectedValues.map(p => p.productId).join(",") : null;
  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function campaignsOnChange(selectedIds: string[], page: string) {
  const pageState : PageState = StateHelper.getPageState();
  pageState.campaignIds = selectedIds.length > 0 ? selectedIds.join(",") : null;
  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function demandSourcesOnChange(selectedValues: DemandSourceRecord[], page : string, isDirectSet?: boolean) {
  const pageState : PageState = StateHelper.getPageState();
  FiltersEventsHelper.demandSorucesDropdownOnChange( page, pageState, selectedValues, isDirectSet);
}

function cLinkOwnersOnChange(selectedValues: CLinkOwnerOption[], page: string, cLinksRef:any, searchCLinkOwnersFunction:(query:string) => void) {
  cLinksRef.current.setState({...cLinksRef.current.state, text: ""});
  searchCLinkOwnersFunction("");
  const pageState : PageState = StateHelper.getPageState();
  pageState.cLinks = selectedValues.length > 0
    ? selectedValues.map(c => PagesHelper.fromCLinkOwnerOptionToString(c))
    : null;
  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function queryFlagsOnChange(selectedValues: QueryFlags[], page : string) {
  const pageState : PageState = StateHelper.getPageState();
  const newFlags = [...(pageState.queryFlags || [])];

  selectedValues.forEach((selectedValue: QueryFlags) => {
    const index = newFlags.indexOf(selectedValue);
    if (index !== -1) {
      newFlags.splice(index, 1);
    } else {
      newFlags.push(selectedValue);
    }
  });

  pageState.queryFlags = newFlags;
  const searchFunction = StateHelper.getSearchFuntion(page);
  searchFunction();
}

function includeExcludeOnChange(field: string, includeExclude: IncludeExclude, page: string) {
  const pageState:PageState = StateHelper.getPageState();
  let propertyIncludeExclude = field + 'IncludeExclude';
  let valuesField = field;

  if (field === 'publishers') {
    propertyIncludeExclude = 'placementsIncludeExclude';
  } else if (field === 'trafficSources') {
    propertyIncludeExclude = 'trafficSourcesIncludeExclude';
    valuesField = 'publisherIds'; // Check publisherIds for values
  } else if (field === 'productIds') {
    propertyIncludeExclude = 'productsIncludeExclude';
  }

  (pageState as any)[propertyIncludeExclude] = includeExclude;

  // For traffic sources, check both publisherIds and siteIds
  let hasValues = false;
  if (field === 'trafficSources') {
    hasValues = (pageState.publisherIds && pageState.publisherIds.length > 0) ||
                (pageState.siteIds && pageState.siteIds.length > 0);
  } else {
    const values = (pageState as any)[valuesField];
    hasValues = values && values.length > 0;
  }

  if (hasValues) {
    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  }
}

export const FiltersHelper = {
  getCampaignsCard : function(
    currentUser : any,
    page : string,
    campaignIdsRef : any,
    dto : ProductsCampaignsDto,
    searchProductsCampaigns?: (query: string) => void,
    isLoading?: boolean
  ){
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("campaignsInclude");
    if( ! showFilter ){
      return null;
    }

    let component : any = null;

    if( dto && dto.status === ResponseStatus.ERROR ){
      component = <UnableToLoadMessage message="Unable to load Campaigns." />;
    }
    else {
      // Always render CampaignsAutocomplete to preserve input state during loading
      const pageState : PageState = StateHelper.getPageState();
      const campaignIdsSelected : string[] = pageState.campaignIds ? pageState.campaignIds.split(",") : [];
      const options = dto?.campaignsResponse || [];

      component = <CampaignsAutocomplete
                      onChange={(ids : string[]) => campaignsOnChange(ids, page)}
                      options={options}
                      selected={campaignIdsSelected}
                      reactRef={campaignIdsRef}
                      disabled={false}
                      multiple={true}
                      placeholder="Select Campaigns"
                      defaultIncludeExclude={pageState.campaignIdsIncludeExclude}
                      includeExcludeOnChange={(e : any) => includeExcludeOnChange('campaignIds', e, page)}
                      onInputChange={searchProductsCampaigns}
                      isLoading={isLoading}
                    />;
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Campaigns{' '}
          <InfoIcon>
            <span>Filter by specific campaign IDs. Select one or multiple campaigns to analyze their performance.</span>
          </InfoIcon>
        </Card.Title>
        {component}
      </Card>
    );
  },

  getProductsCard : function(
    currentUser : any,
    page : string,
    productsRef : any,
    dto : ProductsCampaignsDto,
    searchProductsCampaigns?: (query: string) => void,
    isLoading?: boolean
  ){
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("productIds") || currentUser.filterFields.includes("productIdsInclude");
    if( ! showFilter ){
      return null;
    }

    let component : any = null;

    if( dto && dto.status === ResponseStatus.ERROR ){
      component = <UnableToLoadMessage message="Unable to load Products" />;
    }
    else {
      // Always render ProductsAutocomplete to preserve input state during loading
      const pageState : PageState = StateHelper.getPageState();
      const productsSelected : ProductRecord[] = dto ? FiltersInitialStateHelper.getProductsState(dto.productsResponse) : [];
      const options = dto?.productsResponse || [];

      component = <ProductsAutocomplete
                      onChange={(e : any) => productsOnChange(e, page)}
                      options={options}
                      selected={productsSelected}
                      reactRef={productsRef}
                      disabled={false}
                      defaultIncludeExclude={pageState.productsIncludeExclude}
                      includeExcludeOnChange={(e : any) => includeExcludeOnChange('productIds', e, page)}
                      onInputChange={searchProductsCampaigns}
                      isLoading={isLoading}
                    />;
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Products{' '}
          <InfoIcon>
            <span>Focus your analysis on specific products. You can select one or multiple domain identifiers. All domains use the Pluto name convention "tile.domain".</span>
          </InfoIcon>
        </Card.Title>
        {component}
      </Card>
    );
  },

  getDemandSourcesCardForProductsPage : function(currentUser: any, page: string, demandSourcesRef: any, dto: DemandSourcesResponseDto, forceDisabled: boolean = false){
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    let component : any = null;

    // Show loading message if dto is not loaded yet
    if( !dto ){
      component = <LoadingMessage message="Loading Demand Sources..."/>;
    } else {
      const showError : boolean = dto.status === ResponseStatus.ERROR;

      if( showError ){
        component= <UnableToLoadMessage message="Unable to load Demand Sources" />;
      }
      else{
        const pageState : PageState = StateHelper.getPageState();
        const demandSourcesSelected : DemandSourceRecord[] = FiltersInitialStateHelper.getDemandSourcesState(dto.demandSourcesResponse);
        const cmbDemandSources = <DemandSourcesAutocomplete
                                  onChange={(e : any, isDirectSet?: boolean) => demandSourcesOnChange(e, page, isDirectSet)}
                                  options={dto.demandSourcesResponse}
                                  selected={demandSourcesSelected}
                                  selectedForCheckboxes={demandSourcesSelected}
                                  reactRef={demandSourcesRef}
                                  disabled={forceDisabled}
                                  multiple
                                  useGrouped={true}
                                  useCheckbox={true}
                                  useIncludeExclude
                                  defaultIncludeExclude={pageState.demandSourceIdsIncludeExclude}
                                  includeExcludeOnChange={(e : any) => includeExcludeOnChange('demandSourceIds', e, page)}
                                />;
        component = cmbDemandSources;
      }
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Demand Sources{' '}
          <InfoIcon>
            <span>Filter by demand partner. Select one or multiple demand sources to analyze traffic from specific demand partners.</span>
          </InfoIcon>
        </Card.Title>
        {component}
      </Card>
    );
  },

  getGroupByCard : function(currentUser : any, page : string, groupByRef : any){
    const allowedGroupByFields : string[] = currentUser && currentUser.groupByFields ? currentUser.groupByFields : [];
    const groupByState = FiltersInitialStateHelper.getGroupByState(page,allowedGroupByFields);
    let GROUP_BY_ARRAY = getGroupByArray(page,allowedGroupByFields,groupByState);

    const pageState = StateHelper.getPageState();

    if (pageState.pivotBy) {
      // remove date-based options since the pivot doesn't allow for toggling between using the group-by control
      GROUP_BY_ARRAY = GROUP_BY_ARRAY.filter((option) => !isDateGroupBy(option.id));
    }

    const datesMonthBounded = pageState.dateStart.isSame(pageState.dateStart.startOf("month"), "day")
        && pageState.dateEnd.isSame(pageState.dateEnd.endOf("month").startOf("day"), "day");
    const datesWeekBounded = pageState.dateStart.isSame(pageState.dateStart.startOf("isoWeek"), "day")
        && pageState.dateEnd.isSame(pageState.dateEnd.endOf("isoWeek"), "day");

    // if the current date range is not week bounded, then don't allow the week group-by option
    if ( !datesWeekBounded ) {
      GROUP_BY_ARRAY = GROUP_BY_ARRAY.filter((option) => option.id !== ColumnIds.week);
    }

    // if the current date range is not month bounded, then don't allow the month group-by option
    if ( !datesMonthBounded ) {
      GROUP_BY_ARRAY = GROUP_BY_ARRAY.filter((option) => option.id !== ColumnIds.month);
    }

    // if too many days are included in this date range, donm't allow the day or week group-by options
    if ( pageState.dateEnd.diff(pageState.dateStart, "days") > 92 ) {
      GROUP_BY_ARRAY = GROUP_BY_ARRAY.filter((option) => option.id !== ColumnIds.date && option.id !== ColumnIds.week);
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%", height: "20px" }}>
          Group By{' '}
          <InfoIcon>
            <span>Segment your report by one or more dimensions. Each selected grouping adds new columns to the left of your results table, allowing you to analyze performance at a granular level.</span>
          </InfoIcon>
        </Card.Title>
        <GroupByAutocomplete id="cmbGroupBy"  onChange={(e : any) => groupByOnChange(e, page)} selected={groupByState} options={GROUP_BY_ARRAY} reactRef={groupByRef} />
      </Card>
    );
  },

  getPublisherPlacementsCard: function(
      currentUser : any,
      page : string,
      placementsRef : any,
      trafficSourceResponseDto : TrafficSourceResponseDto,
      setPlacementsResponseDto : any,
      placementResponseDto: PlacementResponseDto,
      placementsIsLoading: any,
      setPlacementsIsLoading : any) {

    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("placementsInclude") ;
    if( ! showFilter ){
      return null;
    }

    let placementOptions : PlacementRecord[] = [];
    let selectedPlacementRecords : PlacementRecord[] = [];

    const searchPlacementsFunction = ( query : string) => {
      CommonRequests.searchPlacements(query, setPlacementsResponseDto, setPlacementsIsLoading)
    };
    
    if( placementResponseDto && placementResponseDto.status === ResponseStatus.ERROR ){
      selectedPlacementRecords = [];
      placementOptions =  placementResponseDto ? placementResponseDto.placementsResponse : [];
    }
    else {
      selectedPlacementRecords = FiltersInitialStateHelper.getPublisherPlacementsInitialState(trafficSourceResponseDto);
      placementOptions =  placementResponseDto ? placementResponseDto.placementsResponse : [];
    }
    const pageState : PageState = StateHelper.getPageState();

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Placements{' '}
          <InfoIcon>
            <span>Filter by Publisher Placement ID or SubID. Start typing a placement ID (3+ characters) to search and isolate information from a specific source.</span>
          </InfoIcon>
        </Card.Title>
        <PlacementsAutocomplete 
          onChange={(e : any) => placementsOnChange(e, page)} 
          selected={selectedPlacementRecords} 
          options={placementOptions}
          reactRef={placementsRef} 
          searchPlacements={searchPlacementsFunction} 
          placementsIsLoading={placementsIsLoading}
          defaultIncludeExclude={pageState.placementsIncludeExclude}
          includeExcludeOnChange={(e : any) => includeExcludeOnChange('placements', e, page)}
          useIncludeExclude={isAllowedFilterField("placementsExclude")}
        />
      </Card>
    );
  },
  
  getTrafficTypesCard : function(currentUser : any, page : string, trafficTypesRef : any) : any {
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("trafficTypes") || currentUser.filterFields.includes("trafficTypesInclude") ;
    if( ! showFilter ){
      return null;
    }

    const trafficTypesState = FiltersInitialStateHelper.getTrafficTypesState();
    const pageState : PageState = StateHelper.getPageState();

    return(
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Traffic Types{' '}
          <InfoIcon>
            <span>Narrow your report by the type of device or operating system generating the traffic.</span>
          </InfoIcon>
        </Card.Title>
        <TrafficTypeAutocomplete
          onChange={(e : any) => trafficTypeOnChange(e, page)}
          selected={trafficTypesState}
          reactRef={trafficTypesRef}
          defaultIncludeExclude={pageState.trafficTypesIncludeExclude}
          includeExcludeOnChange={(e: any) => includeExcludeOnChange('trafficTypes', e, page)}
        />
      </Card>
    );
  },

  getCountriesCard : function(currentUser : any, page : string, countriesRef : any) : any {
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("countries") || currentUser.filterFields.includes("countriesInclude");
    if( ! showFilter ){
      return null;
    }

    const countriesState = FiltersInitialStateHelper.getCountriesState();
    const pageState : PageState = StateHelper.getPageState();

      return(
        <Card style={{border: "none"}}>
          <Card.Title style={{ fontSize: "100%"}}>
            Countries{' '}
            <InfoIcon>
              <span>Narrow results by the geographic origin of traffic. Select one or multiple country codes to filter where clicks originated.</span>
            </InfoIcon>
          </Card.Title>
          <CountriesAutocomplete
            onChange={(e : any) => countryOnChange(e, page)}
            selected={countriesState}
            reactRef={countriesRef}
            multiple
            useIncludeExclude
            defaultIncludeExclude={pageState.countriesIncludeExclude}
            includeExcludeOnChange={(e : any) => includeExcludeOnChange('countries', e, page)}
          />
        </Card>
      );
    },

  getDateRangeCard : function(currentUser : any, page : string, additionalTooltipText? : string) : any {
    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("dateStart") || currentUser.filterFields.includes("dateEnd");
    if( ! showFilter ){
      return null;
    }

    const dateRange = FiltersInitialStateHelper.getDateRangeState();

    const statusIcon = <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: "14px", color: "#9fcdff" }} fixedWidth></FontAwesomeIcon>;

    const title = "Dates";
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%", height: "20px" }}>
          {title}
          <OverlayTrigger
            key="dates-tooltip-overlay"
            placement="right"
            overlay={( props ) => {
              const todayUTC = dayjs.utc().format("YYYY-MM-DD");
              const todayLocal = dayjs().format("YYYY-MM-DD");
              return (
                <Tooltip {...props} id="dates-tooltip">
                  <div>Reporting is in GMT (UTC).</div>
                  <div>For reporting purposes, "today" is: {todayUTC}.</div>
                  {todayLocal === todayUTC ? null : <div>Local "today" is {todayLocal}.</div> }
                  {additionalTooltipText && <div style={{ marginTop: '8px' }}>{additionalTooltipText}</div>}
                </Tooltip>
              );
            }}>
            <span>{statusIcon}</span>
          </OverlayTrigger>
        </Card.Title>
        <DateRangeSelector onChange={dateRangeOnChange} startDate={dateRange.dateStart} endDate={dateRange.dateEnd}/>
      </Card>             
    );
  },

  getTrafficSourcesCard : function(
    currentUser: any,
    page: string,
    trafficSourceResponseDto: TrafficSourceResponseDto,
    trafficSourcesRef: any,
    forceDisabled: boolean = false): any {

    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("placementsInclude");
    if( ! showFilter ){
      return null;
    }

    const pageState : PageState = StateHelper.getPageState();
    const isTrafficSourceAutocompleteDisabled = forceDisabled || (pageState.placementIds && pageState.placementIds.length > 0);
    const onMenuToggle = (isOpen:boolean) => {
      if (!isOpen) trafficSourcesRef.current.setState((prevState:any) => ({...prevState,text: ""}));
    }

    let cmbTrafficSources : any = null;
 
    if ( trafficSourceResponseDto ){
      const showError : boolean = trafficSourceResponseDto.status === ResponseStatus.ERROR;
      if( showError ){
        cmbTrafficSources = <UnableToLoadMessage message="Unable to load Traffic Sources" />;
      }
      else if( trafficSourceResponseDto.publishersArray &&  trafficSourceResponseDto.publishersArray.length > 0 ){
        const publishersArray :  PublisherOrSiteOption[] = trafficSourceResponseDto.publishersArray;
        const selected : PublisherOrSiteOption[] = FiltersInitialStateHelper.getTrafficSourceState(publishersArray);
        cmbTrafficSources =
          <TrafficSourcesAutocomplete
            onChange={(e : PublisherOrSiteOption[], isSelectedFromCheckbox?: boolean) => trafficSourceOnChange(e, page, isSelectedFromCheckbox, publishersArray)}
            options={publishersArray}
            selected={selected}
            selectedForCheckboxes={selected}
            reactRef={trafficSourcesRef}
            disabled={isTrafficSourceAutocompleteDisabled}
            onMenuToggle={onMenuToggle}
            useIncludeExclude
            defaultIncludeExclude={pageState.trafficSourcesIncludeExclude}
            includeExcludeOnChange={(e : any) => includeExcludeOnChange('trafficSources', e, page)}
            multiple
            useCheckbox
          />;
      }
    }            
    else {
      cmbTrafficSources = <LoadingMessage message="Loading Traffic Sources..."/>;
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%"}}>
          Traffic Sources{' '}
          <InfoIcon>
            <span>Select one or more publishers or publisher sites. Displays both Pluto IDs and readable names.</span>
          </InfoIcon>
        </Card.Title>
        {cmbTrafficSources}
      </Card>
      );

  },

  getCLinkOwnersCard : function(
    currentUser : any,
    page : string,
    cLinksRef : any,
    cLinkOwnerstsIsLoading : boolean,
    setCLinkOwnersIsLoading : any,
    dto : CLinkOwnerResponseDto,
    setCLinkOwnersDto : any,
    trafficSourceResponseDto: any,
    mostFrequentTags:string[]) {

    const validUser : boolean = currentUser && currentUser.filterFields;
    if( ! validUser ){
      return null;
    }

    const showFilter : boolean = currentUser.filterFields.includes("clinksInclude") && (currentUser.permissions.includes(UserPermissions.appptapDataCLinks) || currentUser.permissions.includes(UserPermissions.appptapDataCLinksOwners));
    if( ! showFilter ){
      return null;
    }

    const searchCLinkOwnersFunction = async( query : string) => {
      setCLinkOwnersIsLoading(true);
      const results = await CommonRequests.searchCLInkOwners(query);
      if (results) {
        setCLinkOwnersIsLoading(false);
        setCLinkOwnersDto(results);
      }
    };
    const placeholder = currentUser.permissions.includes(UserPermissions.appptapDataCLinksOwners) ? "Specify cLinkID or Owner to search for" : "Specify cLinkID to search for";

    let component : any = null;
    const cLinkOwnersOptions: CLinkOwnerOption[] = dto && dto.cLinksOwnerResponse ? dto.cLinksOwnerResponse as CLinkOwnerOption[] : [];
    const cLinkOwnerSelected = FiltersInitialStateHelper.getCLinkOwnersState(page, cLinkOwnersOptions);
    const pageState: PageState = StateHelper.getPageState();

    if (dto) {
      component = <CLinkOwnersAutocomplete 
              id="cmbCLinks" 
              onChange={(e : any) => cLinkOwnersOnChange(e, page, cLinksRef, searchCLinkOwnersFunction)} 
              options={cLinkOwnersOptions} 
              selected={cLinkOwnerSelected}
              onInputChange={searchCLinkOwnersFunction}
              searchCLinkOwners={searchCLinkOwnersFunction} 
              reactRef={cLinksRef} 
              disabled={false}
              placeholder={placeholder}
              trafficSourceResponseDto={trafficSourceResponseDto}
              clinkOwnersIsLoading={cLinkOwnerstsIsLoading}
              useIncludeExclude={isAllowedFilterField("clinksExclude")}
              defaultIncludeExclude={pageState.cLinksIncludeExclude}
              includeExcludeOnChange={(e : any) => includeExcludeOnChange('cLinks', e, page)}
            />;
    } else {
      component = <LoadingMessage message="Loading Clinks and Owners.."/>;
    }

    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%", height: "20px", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "clip"}}>
          cLinks{' '}
          <InfoIcon>
            <span>Filter using a specific cLink ID or cLink Owner. Use this when troubleshooting or validating specific user flows.</span>
          </InfoIcon>
        </Card.Title>
        {component}
      </Card>
    );
  },

  getQueryFlagsCard: function(currentUser : any, page : string, queryFlagsRef : any) : any {
    const hasCampaignsPermissions : boolean = currentUser && currentUser.permissions && currentUser.permissions.includes(UserPermissions.apptapDataCampaigns);
    if ( !hasCampaignsPermissions ) {
      return null;
    }

    const queryFlagsState = FiltersInitialStateHelper.getQueryFlagsState();

    return(
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%", height: "20px" }}>
          Query Flags{' '}
          <InfoIcon>
            <span>Additional checkbox filters to refine your results based on monetization and conversion criteria.</span>
          </InfoIcon>
        </Card.Title>
        <QueryFlagsAutocomplete
          onChange={(e : QueryFlags[]) => queryFlagsOnChange(e,page)}
          selected={queryFlagsState}
          reactRef={queryFlagsRef}
        />
      </Card>
    );
  },

  checkRequiredGroupByAndPermissions: function(currentUser : any, page: string) {
    const allowedGroupByFields : string[] = currentUser && currentUser.groupByFields ? currentUser.groupByFields : [];
    const groupByState = FiltersInitialStateHelper.getGroupByState(page,allowedGroupByFields);
    const GROUP_BY_ARRAY = getGroupByArray(page,allowedGroupByFields,groupByState);

    const pageState : PageState = StateHelper.getPageState();
    const prevGroupBy = pageState.groupBy;
    pageState.groupBy = "";
    groupByState.forEach((groupBy) => {
      if (!GROUP_BY_ARRAY.find((grooupByOption:any) => grooupByOption.id === groupBy.id)) return;
      pageState.groupBy = pageState.groupBy ?  pageState.groupBy + "," + groupBy.id : pageState.groupBy + groupBy.id;
    });
    GROUP_BY_ARRAY.forEach((groupBy) => {
      if (groupBy.required && !groupByState.includes(groupBy)) {
        if (!pageState.groupBy) {
          pageState.groupBy = groupBy.id;
        } else {
          pageState.groupBy = groupBy.id + "," + pageState.groupBy;
        }
      }
    });

    if (prevGroupBy !== pageState.groupBy) {
      // use of history replace instead of push to keep browser back and forward working
      PagesHelper.historyReplaceFromPageState();
    }
  },

  // Brands-specific filter methods
  getCampaignStatsCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Campaign Stats{' '}
          <InfoIcon>
            <span>Filter by campaign operational readiness - whether campaigns are live, approved, properly configured, and their geographic targeting settings</span>
          </InfoIcon>
        </Card.Title>
        <CampaignStatsAutocomplete
          onChange={(selected: CampaignStatsOption[]) => 
            setFilters({...filters, selectedCampaignStats: selected})
          }
          selected={filters.selectedCampaignStats}
          reactRef={reactRef}
          multiple={true}
        />
      </Card>
    );
  },

  getCampaignHealthCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Campaign Health{' '}
          <InfoIcon>
            <span>Filter by campaign technical health - quality control testing results and conversion rate performance to identify working vs broken campaigns</span>
          </InfoIcon>
        </Card.Title>
        <CampaignHealthAutocomplete
          onChange={(selected: CampaignHealthOption[]) => 
            setFilters({...filters, selectedCampaignHealth: selected})
          }
          selected={filters.selectedCampaignHealth}
          reactRef={reactRef}
          multiple={true}
        />
      </Card>
    );
  },

  getActivityStatusCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Recent Activity{' '}
          <InfoIcon>
            <span>Filter by recent traffic activity and performance</span>
          </InfoIcon>
        </Card.Title>
        <ActivityStatusAutocomplete
          onChange={(selected: ActivityStatusOption[]) => 
            setFilters({...filters, selectedActivityStatus: selected})
          }
          selected={filters.selectedActivityStatus}
          reactRef={reactRef}
          multiple={true}
        />
      </Card>
    );
  },

  getClinksStatusCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          cLink Presence{' '}
          <InfoIcon>
            <span>Filter products or campaigns by whether they have cLinks or not</span>
          </InfoIcon>
        </Card.Title>
        <ClinksStatusAutocomplete
          onChange={(selected: ClinksStatusOption[]) => 
            setFilters({...filters, selectedClinksStatus: selected.length > 0 ? selected[0] : null})
          }
          selected={filters.selectedClinksStatus ? [filters.selectedClinksStatus] : []}
        />
      </Card>
    );
  },
  
  getUniqueSourcesCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Unique sources{' '}
          <InfoIcon>
            <span>Filter products by how many different demand sources they have campaigns from</span>
          </InfoIcon>
        </Card.Title>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
          <Form.Control
            type="number"
            placeholder="Min"
            value={filters.minDemandSources || ''}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value) : null;
              setFilters({...filters, minDemandSources: value});
            }}
            size="sm"
            min="0"
            style={{ width: '70px' }}
          />
          <span style={{ color: '#6c757d' }}>-</span>
          <Form.Control
            type="number"
            placeholder="Max"
            value={filters.maxDemandSources || ''}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value) : null;
              setFilters({...filters, maxDemandSources: value});
            }}
            size="sm"
            min="0"
            style={{ width: '70px' }}
          />
        </div>
      </Card>
    );
  },

  getDemandSourcesCardForLookupPage: function(filters: any, setFilters: any, reactRef: any, demandSourcesParam?: any[]) {
    const demandSources = demandSourcesParam || StateHelper.getDemandSourcesState() || [];
    
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Demand Sources{' '}
          <InfoIcon>
            <span>Filter by external advertising partners that provide campaigns and receive clicks from the platform</span>
          </InfoIcon>
        </Card.Title>
        {demandSources.length === 0 ? (
          <LoadingMessage message="Loading Demand Sources..." />
        ) : (
          <DemandSourcesAutocomplete
            onChange={(selected: DemandSourceRecord[], isDirectSet?: boolean) => {
              const originalSelected = filters.selectedDemandSources || [];
              
              // If explicitly marked as direct set (from X buttons), or empty array
              if (isDirectSet || selected.length === 0) {
                setFilters({...filters, selectedDemandSources: selected});
                return;
              }
              
              // Use toggle pattern for checkbox clicks
              const selectedDemandSources = [...originalSelected];

              selected.forEach((selectedValue: DemandSourceRecord) => {
                // Always check against the original array, not the modified one
                const index = originalSelected.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);
                if (index !== -1) {
                  // Item exists in original, remove it from current
                  const currentIndex = selectedDemandSources.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);
                  if (currentIndex !== -1) {
                    selectedDemandSources.splice(currentIndex, 1);
                  }
                } else {
                  // Item doesn't exist in original, add it if not already added
                  const alreadyAdded = selectedDemandSources.findIndex((existing: DemandSourceRecord) => existing.demandSourceId === selectedValue.demandSourceId);
                  if (alreadyAdded === -1) {
                    selectedDemandSources.push(selectedValue);
                  }
                }
              });

              setFilters({...filters, selectedDemandSources: selectedDemandSources});
            }}
            options={demandSources}
            selected={filters.selectedDemandSources || []}
            selectedForCheckboxes={filters.selectedDemandSources || []}
            reactRef={reactRef}
            multiple
            useGrouped={true}
            useCheckbox={true}
          />
        )}
      </Card>
    );
  },

  getBrandsCountriesCard: function(filters: any, setFilters: any, reactRef: any) {
    return (
      <Card style={{border: "none"}}>
        <Card.Title style={{ fontSize: "100%" }}>
          Countries{' '}
          <InfoIcon>
            <span>Filter by target countries</span>
          </InfoIcon>
        </Card.Title>
        <CountriesAutocomplete
          selected={filters.selectedCountries}
          onChange={(selected: CountryOption[]) => 
            setFilters({...filters, selectedCountries: selected})
          }
          reactRef={reactRef}
          multiple={true}
        />
      </Card>
    );
  }
};